import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, Grid, styled, Typography, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  IUserDTO,
  postSMTPConfig,
  getSMTPConfig,
  updateImapConfig,
  deleteSMTPConfig,
  poolingForEmails,
  validatingImapConfiguration,
  getInboxPaginated,
} from 'wisestep-api';
import {
  Item,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsColors,
  WsDialog,
  WsDialogSize,
  ControlledTextInput,
  WsSelect,
  WsButton,
  WsDotColors,
  WsDotIcon,
  WsDataTable,
  TagsInput,
  WsSelectObj,
  Label,
  CardVariant,
  SmtpSetupConfigIcon,
  WsCard,
} from 'wisestep-base';

import { useFetchRecruiterSMTPAccounts } from '../../../../components/BulkEmail/helper/apiCalls';
import { SettingsHeader } from '../../../../components/SettingsHeader';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { useUserContext } from '../../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../../features/teams/helper/defaultValues';
import { warning, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import WsSmtpSetup from '../../WsSmtpSetup';

import { IConfiguredAccountsDTO } from './helper';
import ModalForRedirection from './ModalForRedirection';
import { useNewEmailStore } from './Util/NewEmailStore';

export interface IEmailOption {
  label: string;
  icon?: ReactNode;
  url?: string;
  count?: number | string;
}

export const StyledBox = styled(Grid)(() => ({
  borderColor: 'text.primary',
  ml: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyleBoxComponent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
}));

const ConfigureEmailSync = () => {
  const navigate = useNavigate();
  const accountData = useFetchRecruiterSMTPAccounts();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const setInBoxData = useNewEmailStore((state) => state.setInBoxData);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [openSync, setOpenSync] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const defaultFilters = ['facebook.com', 'instagram.com', 'twitter.com'];

  const [wsCloud, setWSCloud] = useState<string>('Manual');

  const [prop, setProp] = useState<'update' | 'new'>('new');
  const setConfiguredImapAccounts = useNewEmailStore((state) => state.setConfiguredImapAccounts);
  const setUnreadCount = useNewEmailStore((state) => state.setUnreadCount);

  const [configuredAccounts, setConfiguredAccounts] = useState<IConfiguredAccountsDTO[]>([]);
  const [recruiterAccounts, setRecruiterAccounts] = useState<WsSelectObj[]>([]);

  useEffect(() => {
    const getAllConfiguredAccounts = async () => {
      const response = await getSMTPConfig();

      if (Array.isArray(response.data)) {
        setConfiguredAccounts(response.data);
      }
      const smtpAccounts = accountData.data;
      const recruiterAccountsArray: WsSelectObj[] = smtpAccounts.map((val) => ({
        label: val.fromEmail,
        value: val.fromEmail,
      }));
      setRecruiterAccounts(recruiterAccountsArray);
    };
    getAllConfiguredAccounts();
  }, [accountData.data]);
  const methods = useForm();
  const { handleSubmit, reset, setValue } = methods;
  const onUpdateSetterValues = () => {
    if (configuredAccounts) {
      reset();
      setValue('server', configuredAccounts[0].emailServer);
      setValue('port', configuredAccounts[0].emailServerPort);
      setValue('emailSync', configuredAccounts[0].emailSync);
      setValue('smartBcc', configuredAccounts[0].bcc);
      setValue('excludeEmailsOnAndFrom', configuredAccounts[0].excludedDomain.split(','));
      setValue('excludeEmailsFrom', configuredAccounts[0].excludeIncomingDomain.split(','));
      setValue('excludeEmailsTo', configuredAccounts[0].excludeOutgoingDomain.split(','));
    }
  };
  const onSubmit = async (data: IConfiguredAccountsDTO, e?: Event) => {
    e?.preventDefault();
    try {
      if (prop === 'new') {
        const newConfigResponse = await postSMTPConfig(data);
        setNotification(success('Configuration Saved Successfully'));
        validatingImapConfig(newConfigResponse.data);
        getLatestData();
      } else {
        const payload: IConfiguredAccountsDTO = data;
        payload.id = configuredAccounts[0].id;
        const updatedConfigResponse = await updateImapConfig(payload);
        setNotification(success('Configuration Updated Successfully'));
        validatingImapConfig(updatedConfigResponse.data);
        getLatestData();
      }

      setOpenSync(false);
    } catch (e) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const validatingImapConfig = async (data) => {
    if (data) {
      const validationResponse = await validatingImapConfiguration(data.recruiterEmail);
      const loadDataFirstTime = await getInboxPaginated(data.recruiterEmail, 0, '');
      if (validationResponse.data) {
        setInBoxData(loadDataFirstTime.data);
        await poolingForEmails(data.recruiterEmail);
        setNotification(success('Configuration Validated Successfully'));
        setShowModal(true);
      } else {
        setNotification(warning('Configuration Validation Failed'));
      }
    }
  };
  const getLatestData = async () => {
    const response = await getSMTPConfig();
    if (Array.isArray(response.data)) {
      setConfiguredImapAccounts(response.data);
      setConfiguredAccounts(response.data);
    }
  };

  const onFormSubmit = (data: FieldValues) => {
    if (data) {
      const formData: IConfiguredAccountsDTO = {
        recruiterName: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
        recruiterEmail: data.recruiterEmail ?? recruiterAccounts[0].label,
        password: data.password,
        emailServer: data.server,
        emailServerPort: data.port,
        emailSync: 'sync',
        bcc: data.smartBcc ?? '',
        excludedDomain:
          (data?.excludeEmailsOnAndFrom
            ? data?.excludeEmailsOnAndFrom.join()
            : defaultFilters.join()) ?? configuredAccounts[0]?.excludedDomain,
        excludeIncomingDomain:
          (data.excludeEmailsFrom ? data.excludeEmailsFrom.join() : defaultFilters.join()) ??
          configuredAccounts[0]?.excludeIncomingDomain,
        excludeOutgoingDomain:
          (data.excludeEmailsTo ? data.excludeEmailsTo.join() : defaultFilters.join()) ??
          configuredAccounts[0]?.excludeOutgoingDomain,
        availableFields: wsCloud,
        // attachment?: " ",
        status: 'ACTIVE',
        syncDuration: 0,
      };
      onSubmit(formData);
    }
  };
  const columns = useMemo(
    () => [
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (cell: Cell) => (
          <WsDotIcon
            color={cell.value === 'ACTIVE' ? WsDotColors.GREEN : WsDotColors.RED}
            value={cell.value}
          />
        ),
      },
    ],
    []
  );
  const deleteConfiguration = async () => {
    try {
      if (configuredAccounts[0].id) await deleteSMTPConfig(configuredAccounts[0]?.id);
      setNotification(success('Configuration Deleted Successfully'));
      setConfiguredAccounts([]);
      setConfiguredImapAccounts([]);
      setUnreadCount(0);
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const configData = useMemo(
    () =>
      configuredAccounts?.map((configList) => ({
        email: configList.recruiterEmail,
        active: configList.status,
      })),
    [configuredAccounts]
  );
  return (
    <Box borderRadius={1.5}>
      <WsDialog
        onClose={() => setOpenSync(false)}
        openDialog={openSync}
        size={WsDialogSize.LARGE}
        fullScreen={false}
      >
        <Grid container p={2} bgcolor={WsColors.BorderGrey}>
          <Grid item xs={11} display="flex" alignItems={'center'}>
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY}>
              Configure Email Inbox
            </WsTypography>
          </Grid>
        </Grid>
        <FormProvider {...methods}>
          <form onSubmit={(e) => handleSubmit(onFormSubmit)(e)}>
            <Grid direction={'row'} container rowSpacing={2}>
              <Grid item xs={12} p={1}>
                <Item>
                  <WsSelect
                    required={true}
                    name="recruiterEmail"
                    label="Configured Email Accounts*"
                    selectArray={recruiterAccounts}
                    defaultValue={
                      !isEmpty(configuredAccounts)
                        ? configuredAccounts[0].recruiterEmail
                        : !isEmpty(recruiterAccounts)
                        ? recruiterAccounts[0].label
                        : ''
                    }
                  />
                  <li>
                    Important: Please setup your Send Email account First
                    {
                      <WsButton variant="text" onClick={() => navigate('/email-settings/')}>
                        Click Here
                      </WsButton>
                    }
                  </li>
                  <ControlledTextInput
                    name="server"
                    labelText="Mail (IMAP) Server*"
                    type="text"
                    fullWidth={true}
                    id="server"
                    placeholder="For Google/GSuite enter -  imap.google.com / For Microsoft enter - outlook.office365.com"
                    variant="outlined"
                    required={true}
                  />
                  <ControlledTextInput
                    name="port"
                    labelText="Port Number*"
                    type="text"
                    fullWidth
                    id="port"
                    placeholder="993"
                    variant="outlined"
                    required={true}
                  />
                  <ControlledTextInput
                    name="password"
                    labelText="App Password*"
                    type="password"
                    fullWidth={true}
                    id="server"
                    placeholder="Enter your app password"
                    variant="outlined"
                    required={true}
                    helperText={
                      <Label>
                        <li>
                          For Gmail and Google Workspace users please
                          {
                            <WsButton
                              onClick={() =>
                                openUrlInNewTab('https://support.google.com/accounts/answer/185833')
                              }
                              variant="text"
                            >
                              Click Here
                            </WsButton>
                          }
                          to generate App Password.
                        </li>
                        <li>
                          For Microsoft Outlook users please
                          {
                            <WsButton
                              onClick={() =>
                                openUrlInNewTab(
                                  'https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944'
                                )
                              }
                              variant="text"
                            >
                              Click Here
                            </WsButton>
                          }
                          to generate App Password.
                        </li>
                      </Label>
                    }
                  />
                  <Grid marginTop={2}>
                    <WsTypography variant={WsTypoVariants.TITLE}>
                      Smart BCC (If you are adding BCC then it counts against your sending Quota):
                      {
                        <WsButton
                          onClick={() =>
                            openUrlInNewTab(
                              'https://wisestep.freshdesk.com/support/solutions/articles/4000155527'
                            )
                          }
                          variant="text"
                        >
                          Check Your Quota
                        </WsButton>
                      }
                    </WsTypography>
                    <ControlledTextInput
                      name="smartBcc"
                      type="text"
                      fullWidth
                      id="smartBcc"
                      placeholder="Enter an email address you want to bcc on your outgoing emails."
                      variant="outlined"
                      required={false}
                    />
                  </Grid>
                </Item>
                <Item>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    Exclude emails if sent from these domains (incoming email)*:
                  </WsTypography>
                  <TagsInput
                    name={'excludeEmailsFrom'}
                    placeholder={`Enter a domain name Eg: example.com`}
                    multiple={true}
                    initialValue={
                      prop === 'new'
                        ? defaultFilters
                        : configuredAccounts[0].excludeIncomingDomain.split(',')
                    }
                  />
                </Item>

                <Grid item marginTop={3}>
                  <Item>
                    <Typography component="h1" sx={{ mb: 1.5 }}>
                      Capture Attachment on Wisestep*
                    </Typography>
                    <Grid container direction="row" columnGap={1}>
                      <Grid item>
                        <WsButton onClick={() => setWSCloud('Manual')} variant="contained">
                          User Action
                        </WsButton>
                      </Grid>
                    </Grid>
                  </Item>
                </Grid>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Item>
                    <Tooltip title="Save">
                      <Box textAlign="center">
                        <WsButton variant="outlined" type="submit">
                          Save
                        </WsButton>
                      </Box>
                    </Tooltip>
                  </Item>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </WsDialog>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['EMAIL_SETTINGS']
        )}
      />
      <SettingsHeader title="Email Settings" />
      <Grid container spacing={1}>
        <Grid item xs={2.5} marginTop={2} borderRadius={1.5}>
          <WsSmtpSetup />
        </Grid>
        <Grid marginTop={2} item xs={9.5}>
          {configuredAccounts &&
            (isEmpty(configuredAccounts) ? (
              <WsCard
                cardType={CardVariant.WITH_OUT_BG}
                title="No Email Inbox Accounts Configured"
                description="Configure your inbox"
                middleComponent={<SmtpSetupConfigIcon />}
                isButton={true}
                onClick={() => setOpenSync(true)}
                buttonText="Setup now"
                btnStartIcon={<AddOutlinedIcon />}
              />
            ) : (
              <StyleBoxComponent>
                <WsDataTable
                  elevated={true}
                  columns={columns}
                  data={configData}
                  totalDataItems={configData.length}
                  selectable={false}
                  preSelectedRows={[]}
                  pageable={false}
                  rowActions={[
                    {
                      label: 'Update',
                      callback: () => {
                        onUpdateSetterValues();
                        setProp('update');
                        setOpenSync(true);
                      },
                    },
                    {
                      label: 'Delete',
                      callback: () => deleteConfiguration(),
                    },
                  ]}
                />
              </StyleBoxComponent>
            ))}
        </Grid>
      </Grid>
      <ModalForRedirection modalState={showModal} onClose={() => setShowModal(!showModal)} />
    </Box>
  );
};

export default ConfigureEmailSync;
